import type { Question, FormValue } from '../../types';

export interface CoverageSearchResult {
  covered: string[];
  uncovered: string[];
}

/** Every schema QID in this section subtree, including `is_pseudo` and nested `subitems`. */
export function collectSectionCoverageQids(questions: Question[]): string[] {
  const qids: string[] = [];
  const walk = (qs: Question[]) => {
    for (const q of qs) {
      qids.push(q.qid);
      if (q.subitems) {
        for (const subqs of Object.values(q.subitems)) {
          walk(subqs);
        }
      }
    }
  };
  walk(questions);
  return qids;
}

/** Candidate answer values explored to unlock conditional subtrees */
export function buildAnswerCandidates(
  questions: Question[]
): Map<string, FormValue[]> {
  const map = new Map<string, FormValue[]>();
  const add = (qs: Question[]) => {
    for (const q of qs) {
      switch (q.type) {
        case 'radio':
          map.set(q.qid, q.values ?? []);
          break;
        case 'multiselect':
          map.set(
            q.qid,
            (q.values ?? []).map(v => [v] as string[])
          );
          break;
        case 'text':
          map.set(q.qid, ['test']);
          break;
        case 'number':
          map.set(q.qid, [0]);
          break;
        case 'date':
          map.set(q.qid, ['20000101']);
          break;
      }
      if (q.subitems) {
        for (const subqs of Object.values(q.subitems)) {
          add(subqs);
        }
      }
    }
  };
  add(questions);
  return map;
}

export function readRenderedFieldQids(container: ParentNode): string[] {
  return [...container.querySelectorAll('[data-qid]')]
    .map(el => el.getAttribute('data-qid')!)
    .filter(Boolean);
}

/**
 * Exhaustive-ish search mirroring RTL-based checkBespokeCoverage: try answer
 * combinations until no new `[data-qid]` fields appear, then flood remaining.
 */
export async function exploreReachableRenderedFieldQids(options: {
  schema: Question[];
  getRenderedQids: () => string[];
  setAnswers: (answers: Record<string, FormValue>) => Promise<void> | void;
}): Promise<CoverageSearchResult> {
  const { schema, getRenderedQids, setAnswers } = options;
  const allQids = collectSectionCoverageQids(schema);
  const uncovered = new Set(allQids);
  const candidates = buildAnswerCandidates(schema);
  const currentAnswers: Record<string, FormValue> = {};

  const testWith = async (answers: Record<string, FormValue>) => {
    await setAnswers(answers);
    return getRenderedQids();
  };

  getRenderedQids().forEach(q => uncovered.delete(q));

  let madeProgress = true;
  while (uncovered.size > 0 && madeProgress) {
    madeProgress = false;
    for (const [qid, values] of candidates) {
      if (uncovered.size === 0) break;
      for (const value of values) {
        const visible = await testWith({ ...currentAnswers, [qid]: value });
        const newlyVisible = visible.filter(q => uncovered.has(q));
        if (newlyVisible.length > 0) {
          newlyVisible.forEach(q => uncovered.delete(q));
          currentAnswers[qid] = value;
          madeProgress = true;
          break;
        }
      }
    }
  }

  if (uncovered.size > 0) {
    const allSet = { ...currentAnswers };
    for (const [qid, values] of candidates) {
      if (!(qid in allSet) && values.length > 0) {
        const firstCandidate = values[0];
        allSet[qid] = Array.isArray(firstCandidate)
          ? (values as string[][]).flat()
          : firstCandidate;
      }
    }
    (await testWith(allSet)).forEach(q => uncovered.delete(q));
  }

  return {
    covered: allQids.filter(q => !uncovered.has(q)),
    uncovered: [...uncovered],
  };
}
